export type NfeFornecedorParsed = {
  fornecedor: string;
  valor: number;
  dataVencimento: string;
};

const MAX_XML_BYTES = 10 * 1024 * 1024;

export function validateNfeXmlFile(file: File): string | null {
  const ext = file.name.toLowerCase().split(".").pop();
  if (ext !== "xml") {
    return "Apenas arquivos com extensão .xml são aceitos.";
  }
  if (file.size === 0) {
    return "O arquivo XML está vazio.";
  }
  if (file.size > MAX_XML_BYTES) {
    return `Arquivo muito grande (máximo ${Math.round(MAX_XML_BYTES / (1024 * 1024))} MB).`;
  }
  return null;
}

export function getNfeXmlMaxBytes(): number {
  return MAX_XML_BYTES;
}

function readText(node: Element | null | undefined): string {
  return (node?.textContent ?? "").trim();
}

function byLocalName(root: ParentNode, localName: string): Element[] {
  const out: Element[] = [];
  const all = root.querySelectorAll("*");
  for (const n of all) {
    const tag = n.tagName.includes(":") ? n.tagName.split(":").pop() ?? n.tagName : n.tagName;
    if (tag.toLowerCase() === localName.toLowerCase()) {
      out.push(n);
    }
  }
  return out;
}

function firstByLocalName(root: ParentNode, localName: string): Element | null {
  const list = byLocalName(root, localName);
  return list.length > 0 ? list[0] : null;
}

function normalizeNumberBr(v: string): number {
  const txt = v.trim();
  if (!txt) return NaN;
  const n = Number(txt.replace(",", "."));
  return Number.isFinite(n) ? n : NaN;
}

function normalizeIsoDate(v: string): string {
  const s = v.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return s.slice(0, 10);
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
    const [d, m, y] = s.split("/");
    return `${y}-${m}-${d}`;
  }
  return "";
}

export function parseNfeFornecedorXml(xmlText: string): NfeFornecedorParsed {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlText, "application/xml");
  if (doc.querySelector("parsererror")) {
    throw new Error("XML inválido. Verifique o arquivo da NF-e.");
  }

  const emit = firstByLocalName(doc, "emit");
  const fornecedorNome = readText(emit ? firstByLocalName(emit, "xNome") : null);

  const total = firstByLocalName(doc, "total");
  const icmsTot = total ? firstByLocalName(total, "ICMSTot") : null;
  const valorNfTxt = readText(icmsTot ? firstByLocalName(icmsTot, "vNF") : null);
  const valorNf = normalizeNumberBr(valorNfTxt);

  let dataVencimento = "";
  const cobr = firstByLocalName(doc, "cobr");
  if (cobr) {
    const dup = firstByLocalName(cobr, "dup");
    dataVencimento = normalizeIsoDate(readText(dup ? firstByLocalName(dup, "dVenc") : null));
  }
  if (!dataVencimento) {
    dataVencimento = normalizeIsoDate(readText(firstByLocalName(doc, "dVenc")));
  }

  if (!fornecedorNome) {
    throw new Error("Não foi possível identificar o emitente (xNome) no XML da NF-e.");
  }
  if (!Number.isFinite(valorNf) || valorNf <= 0) {
    throw new Error("Não foi possível identificar o valor total da NF-e (vNF).");
  }

  return {
    fornecedor: fornecedorNome,
    valor: Math.round(valorNf * 100) / 100,
    dataVencimento,
  };
}
